import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider } from "antd";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { AN_DARK, AN_LIGHT } from "../design-system/tokens";
import { antdThemeFor } from "../config/theme";

// The console's theme: Light (the default), Dark, or System (follows the
// operating system). The choice is per browser, under Settings → Appearance.
//
// The palette is published as CSS variables (--an-ink, --an-surface, …) on
// <html>, with data-theme="light|dark", so inline styles and stylesheets
// follow it; antd and MUI get the matching theme.

export const THEME_PREFERENCES = ["light", "dark", "system"];
const STORAGE_KEY = "an-theme";

function readPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return THEME_PREFERENCES.includes(v) ? v : "light";
  } catch {
    return "light";
  }
}

const darkQuery = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null);

function resolve(preference) {
  if (preference === "system") return darkQuery()?.matches ? "dark" : "light";
  return preference;
}

export function applyTheme(mode) {
  const root = document.documentElement;
  const palette = mode === "dark" ? AN_DARK : AN_LIGHT;
  Object.entries(palette).forEach(([k, v]) => root.style.setProperty(`--an-${k}`, v));
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
}

// Before the first render, so the page never flashes the other theme.
applyTheme(resolve(readPreference()));

const ThemeContext = createContext({ preference: "light", mode: "light", palette: AN_LIGHT, setPreference: () => {} });

export function useAnTheme() {
  return useContext(ThemeContext);
}

export default function AnThemeProvider({ children }) {
  const [preference, setPref] = useState(readPreference);
  const [systemDark, setSystemDark] = useState(() => !!darkQuery()?.matches);
  const mode = preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    const q = darkQuery();
    if (!q) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    q.addEventListener ? q.addEventListener("change", onChange) : q.addListener(onChange);
    return () => (q.removeEventListener ? q.removeEventListener("change", onChange) : q.removeListener(onChange));
  }, []);

  // Another tab changed the theme.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPref(readPreference());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => applyTheme(mode), [mode]);

  const setPreference = useCallback((p) => {
    if (!THEME_PREFERENCES.includes(p)) return;
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // Private window: the choice lasts for this tab only.
    }
    setPref(p);
  }, []);

  const value = useMemo(
    () => ({ preference, mode, palette: mode === "dark" ? AN_DARK : AN_LIGHT, setPreference }),
    [preference, mode, setPreference],
  );
  const antd = useMemo(() => antdThemeFor(mode), [mode]);
  // message.*, Modal.confirm() and notification.* render outside the tree.
  useEffect(() => {
    ConfigProvider.config({ holderRender: (children) => <ConfigProvider theme={antd}>{children}</ConfigProvider> });
  }, [antd]);
  const mui = useMemo(() => {
    const p = mode === "dark" ? AN_DARK : AN_LIGHT;
    return createTheme({
      palette: {
        mode,
        primary: { main: p.primary },
        background: { default: p.bg, paper: p.surface },
        text: { primary: p.ink, secondary: p.muted },
        divider: p.line,
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antd}>
        <MuiThemeProvider theme={mui}>{children}</MuiThemeProvider>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
